/**
 * SessionContextSummary — inline chip rendered in AttentionInspector.
 *
 * Gives operators an at-a-glance hint of agent activity for an attention item
 * that's linked to a session, plus a button that opens the full
 * AgentSessionModal popout.
 *
 * Behaviour:
 *   - Reads the session's transcript from `useSessionsStore.transcriptCache`
 *     keyed by sessionId. We do NOT auto-fetch the transcript here — keeps
 *     the inspector cheap on large queues. If the modal has loaded the
 *     transcript already, the chip shows turn count + tool count + last
 *     activity. Otherwise it shows a neutral placeholder.
 *   - The "View Agent Session" button always opens the modal; the modal
 *     itself triggers the fetch.
 */

import { useCallback, type CSSProperties, type ReactNode } from 'react'
import { Bot, MessageSquare, Wrench } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { useSessionsStore } from '@/features/sessions/store'
import { useQueueStore } from '../../store'
import { formatRelativeTime } from '../../utils/time'

/**
 * Mirror of `TURN_CAP` in
 * `apps/stream/src/features/queue/components/AgentSessionModal/AgentSessionModalBody.tsx`.
 * Keep in sync — the prefetch's batch size must match the modal's initial
 * load size, otherwise the modal will fire a second request anyway.
 */
const PREFETCH_LIMIT = 200

// ── Props ────────────────────────────────────────────────────────────────────

export interface SessionContextSummaryProps {
  /** The agent session id linked to this attention item. */
  sessionId: string
}

// ── Component ────────────────────────────────────────────────────────────────

export function SessionContextSummary({ sessionId }: SessionContextSummaryProps): ReactNode {
  const cache = useSessionsStore((s) => s.transcriptCache.get(sessionId) ?? null)
  const loadTranscriptChunk = useSessionsStore((s) => s.loadTranscriptChunk)
  const openAgentSessionModal = useQueueStore((s) => s.openAgentSessionModal)

  const turns = cache?.turns ?? []
  const turnCount = turns.length
  const toolCount = turns.filter((t) => t.role === 'tool').length
  const lastTurn = turns.length > 0 ? turns[turns.length - 1] : null

  const hasData = turnCount > 0

  // Pre-fetch the transcript when the operator hovers (or focuses) the
  // "View Agent Session" button — by the time they actually click, the
  // request is already in flight or complete and the modal can render
  // immediately. The store's `loadTranscriptChunk` is idempotent, so
  // hovering repeatedly does not produce duplicate requests.
  const prefetchTranscript = useCallback(() => {
    loadTranscriptChunk(sessionId, PREFETCH_LIMIT)
  }, [sessionId, loadTranscriptChunk])

  return (
    <div style={styles.container} data-testid="session-context-summary">
      <div style={styles.header}>
        <Bot size={12} style={styles.headerIcon} />
        <span style={styles.headerLabel}>Agent activity</span>
      </div>

      <div style={styles.body}>
        {hasData ? (
          <div style={styles.stats}>
            <span style={styles.stat}>
              <MessageSquare size={11} style={styles.statIcon} />
              {turnCount} turn{turnCount === 1 ? '' : 's'}
            </span>
            <span style={styles.statSeparator}>·</span>
            <span style={styles.stat}>
              <Wrench size={11} style={styles.statIcon} />
              {toolCount} tool call{toolCount === 1 ? '' : 's'}
            </span>
            {lastTurn && (
              <>
                <span style={styles.statSeparator}>·</span>
                <span style={styles.stat}>
                  last activity {formatRelativeTime(lastTurn.timestamp)}
                </span>
              </>
            )}
          </div>
        ) : (
          <div style={styles.placeholder}>
            Click below to inspect the agent's reasoning, tool calls, and the
            full chain of work that led to this request.
          </div>
        )}
      </div>

      <Button
        variant="secondary"
        size="sm"
        onClick={() => openAgentSessionModal(sessionId)}
        onMouseEnter={prefetchTranscript}
        onFocus={prefetchTranscript}
        style={styles.button}
        data-testid="view-agent-session-button"
      >
        <MessageSquare size={13} style={{ marginRight: '6px' }} />
        View Agent Session
      </Button>
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  headerIcon: {
    color: 'var(--text-muted)',
  },
  headerLabel: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-muted)',
  },
  body: {
    minHeight: '20px',
  },
  stats: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px',
    fontSize: '12px',
    color: 'var(--text-secondary)',
  },
  stat: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  statIcon: {
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
  statSeparator: {
    color: 'var(--text-faint)',
  },
  placeholder: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
  },
  button: {
    alignSelf: 'flex-start',
  },
}

export default SessionContextSummary
